import { Icon } from "@iconify/react";
import { useRef, useState } from "react";
import homeFill from "@iconify/icons-eva/home-fill";
import personFill from "@iconify/icons-eva/person-fill";
import LockFill from "@iconify/icons-eva/lock-fill";
import questionMarkCircleOutline from "@iconify/icons-eva/question-mark-circle-outline";
import playCircleOutline from "@iconify/icons-eva/play-circle-outline";
import { Link as RouterLink } from "react-router-dom";
// material
import { alpha } from "@mui/material/styles";
import {
  Button,
  Box,
  Divider,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
} from "@mui/material";
// components
import MenuPopover from "../../components/MenuPopover";
import account from "../../_mocks_/account";
// helper functions
import Token from "src/util/Token";
import enums from "src/util/enums";
import { useAuth } from "src/hooks/useAuth.hook";

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: "Home",
    icon: homeFill,
    linkTo: "/",
  },
  {
    label: "Profile",
    icon: personFill,
    linkTo: "/profile",
  },
  {
    label: "Change Password",
    icon: LockFill,
    linkTo: "/changepassword",
  },
  {
    label: "FAQ",
    icon: questionMarkCircleOutline,
    linkTo: "/faq",
  },
  {
    label: "Video Tutorials",
    icon: playCircleOutline,
    linkTo: "https://www.youtube.com/playlist?list=PLNsi9ubaA0MAq1Ao97W_hY03zYkjTfn86",
    external: true,
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const { user } = useAuth();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = () => {
    Token.clear();
    window.location.href = enums.path.LOGIN;
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
          backdropFilter: 'blur(15px)',
          border: '2px solid rgba(255,255,255,0.3)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.2) 100%)',
            transform: 'scale(1.08)',
            boxShadow: '0 6px 25px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
            border: '2px solid rgba(255,255,255,0.4)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
        <Avatar
          sx={{ 
            bgcolor: '#1A73E8',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.5rem',
            width: 56,
            height: 56,
            boxShadow: '0 4px 15px rgba(26, 115, 232, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
            border: '3px solid rgba(255,255,255,0.4)',
            '&:hover': {
              bgcolor: '#34A853',
              transform: 'scale(1.05)',
              boxShadow: '0 6px 20px rgba(52, 168, 83, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
              border: '3px solid rgba(255,255,255,0.5)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          alt={user?.firstName}
          src={user?.photoURL}
        />
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            {...(option.external 
              ? {
                  onClick: () => {
                    window.open(option.linkTo, '_blank');
                    handleClose();
                  }
                }
              : {
                  to: option.linkTo,
                  component: RouterLink,
                  onClick: handleClose
                }
            )}
            sx={{ typography: "body2", py: 1, px: 2.5 }}
          >
            {option.icon && (
              <Box
                component={Icon}
                icon={option.icon}
                sx={{
                  mr: 2,
                  width: 24,
                  height: 24,
                }}
              />
            )}

            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button
            fullWidth
            onClick={handleLogout}
            color="inherit"
            variant="outlined"
          >
            Logout
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
